import { scrollToEl } from '../lib/lenis';

// Smooth in-page scrolling, offset for the sticky header.
//
// Menu items come out of WordPress as absolute URLs ("https://site/#pricing"),
// not bare fragments, so a plain [href^="#"] check would miss them and the
// browser would jump instead. Anything pointing at the current page is treated
// as an anchor; links to another page keep navigating normally.
const NAV_OFFSET = -100;

function samePageHash(link: HTMLAnchorElement): string | null {
  const href = link.getAttribute('href') || '';

  if (href.startsWith('#')) {
    return href.length > 1 ? href.slice(1) : null;
  }

  try {
    const url = new URL(link.href, window.location.href);
    const isSamePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    return isSamePage && url.hash.length > 1 ? url.hash.slice(1) : null;
  } catch {
    return null;
  }
}

function target(id: string): HTMLElement | null {
  return document.getElementById(decodeURIComponent(id));
}

export function initAnchors(): void {
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const link = (event.target as HTMLElement | null)?.closest?.<HTMLAnchorElement>('a[href]');
    if (!link || link.target === '_blank') return;

    const id = samePageHash(link);
    if (!id) return;

    const el = target(id);
    if (!el) return;

    event.preventDefault();
    scrollToEl(el, NAV_OFFSET);
    history.pushState(null, '', `#${id}`);
  });

  // Arriving with a hash (e.g. from the blog back to the home page) lands under
  // the sticky header otherwise. The browser performs its own jump after
  // DOMContentLoaded and images can still shift the layout, so the correction
  // runs again once everything has loaded.
  const initial = window.location.hash.slice(1);
  if (!initial) return;

  const correct = (): void => {
    const el = target(initial);
    if (el) scrollToEl(el, NAV_OFFSET);
  };

  window.requestAnimationFrame(correct);
  window.addEventListener('load', () => window.setTimeout(correct, 60), { once: true });
}
